import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  Directive,
  type ElementRef,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';
import { cn, MMSCAN_UI_I18N } from '@mmscan/angular-ui/core';
import { MmscanLoadingService } from '@mmscan/angular-ui/loading';
import type { MmscanNavItem } from '@mmscan/angular-ui/navigation';
import { MMSCAN_NAVBAR, type MmscanNavbarBreakpoint, MmscanSidebar } from '@mmscan/angular-ui/navigation';
import { MmscanProgressBar } from '@mmscan/angular-ui/progress-bar';
import { type MmscanLogo, MmscanLogoImage } from './logo';

let nextId = 0;

// Same breakpoint as the navbar's menu button: below it the section links live in the drawer.
const ASIDE_VISIBLE: Record<MmscanNavbarBreakpoint, string> = {
  md: 'md:block',
  lg: 'lg:block',
  xl: 'xl:block',
};

/** Content for the right side of the app shell's header (notifications, settings, account). */
@Directive({ selector: '[mmscanShellActions]', host: { class: 'flex items-center gap-1' } })
export class MmscanShellActions {}

/** Content for the right side of the app shell's footer (copyright, social links). */
@Directive({ selector: '[mmscanShellFooter]', host: { class: 'flex flex-wrap items-center gap-2' } })
export class MmscanShellFooter {}

/**
 * Page frame shared by the apps: header (logo, menubar, actions), a section sidebar that collapses
 * to icons (button next to its heading),
 * the page in `<main>`, and a footer (logo, content). Includes a "Skip to main content" link and
 * a progress bar under the header while HTTP requests run (`mmscanLoadingInterceptor`).
 * On small screens the menubar and sidebar items move into a drawer behind a menu button.
 *
 * ```html
 * <mmscan-app-shell
 *   [logo]="{ src: 'logos/miportal.svg', darkSrc: 'logos/miportal-white.svg', alt: 'MiPortal', route: '/home' }"
 *   [footerLogo]="{ src: 'logos/mitsubishi.svg', alt: 'Mitsubishi Motors' }"
 *   [items]="navigation()" [sidebarItems]="section().items" [sidebarHeading]="section().title"
 *   [(sidebarCollapsed)]="sidebarCollapsed" accentIcons>
 *   <div mmscanShellActions><app-notifications /><app-settings /><app-logout /></div>
 *   <router-outlet />
 *   <div mmscanShellFooter>© 2026 Mitsubishi Motor Sales of Canada</div>
 * </mmscan-app-shell>
 * ```
 */
@Component({
  selector: 'mmscan-app-shell',
  imports: [...MMSCAN_NAVBAR, MmscanSidebar, MmscanLogoImage, MmscanProgressBar],
  template: `
    <a
      [href]="'#' + mainId"
      class="sr-only rounded-md bg-background px-3 py-2 text-sm font-medium text-foreground focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:outline-2 focus:outline-ring"
      (click)="skipToContent($event)"
    >
      {{ i18n().skipToContent }}
    </a>

    <!-- \`titleBar\`: the header doubles as a frameless window's title bar (see styles.css). -->
    <div class="relative" [class.mmscan-title-bar]="titleBar()">
      <mmscan-navbar
        [items]="items()"
        [ariaLabel]="ariaLabel()"
        [collapseBelow]="collapseBelow()"
        [sectionItems]="sidebarItems()"
        [sectionHeading]="sidebarHeading()"
        [accentIcons]="accentIcons()"
      >
        @if (logo(); as logo) {
          <mmscan-logo mmscanNavbarBrand class="h-6" [logo]="logo" />
        }
        <div mmscanNavbarEnd><ng-content select="[mmscanShellActions]" /></div>
      </mmscan-navbar>
      @if (showLoading() && loading.visible()) {
        <!-- Over the header's bottom edge, so it never shifts the page. -->
        <div class="absolute inset-x-0 -bottom-px z-10"><mmscan-progress-bar size="sm" /></div>
      }
    </div>

    <div class="flex min-h-0 flex-1">
      @if (sidebarItems().length) {
        <aside [class]="asideClasses()">
          <mmscan-sidebar
            collapsible
            [items]="sidebarItems()"
            [heading]="sidebarHeading()"
            [ariaLabel]="i18n().sectionNavigation"
            [(collapsed)]="sidebarCollapsed"
            [accentIcons]="accentIcons()"
          />
        </aside>
      }
      <main
        #main
        [id]="mainId"
        tabindex="-1"
        class="min-w-0 flex-1 overflow-auto outline-none"
        [attr.aria-busy]="(showLoading() && loading.visible()) || null"
      >
        <ng-content />
      </main>
    </div>

    <footer
      class="flex flex-wrap items-center justify-between min-h-8 gap-x-4 border-t border-border px-4 text-xs text-muted-foreground"
    >
      @if (footerLogo(); as logo) {
        <mmscan-logo class="h-3.5" [logo]="logo" />
      }
      <div class="ml-auto"><ng-content select="[mmscanShellFooter]" /></div>
    </footer>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-dvh flex-col bg-background text-foreground' },
})
export class MmscanAppShell {
  protected readonly i18n = inject(MMSCAN_UI_I18N);
  protected readonly loading = inject(MmscanLoadingService);
  private readonly id = nextId++;
  protected readonly mainId = `mmscan-main-${this.id}`;
  private readonly main = viewChild.required<ElementRef<HTMLElement>>('main');

  /** Header logo (app name / wordmark). */
  readonly logo = input<MmscanLogo>();
  /** Footer logo (company). */
  readonly footerLogo = input<MmscanLogo>();
  /** Main navigation, shown as the header menubar. */
  readonly items = input<readonly MmscanNavItem[]>([]);
  /** Name of the main navigation landmark. */
  readonly ariaLabel = input<string>();
  /** Current section's links; the sidebar only shows when there are some. */
  readonly sidebarItems = input<readonly MmscanNavItem[]>([]);
  readonly sidebarHeading = input<string>();
  /** Sidebar collapsed to an icon rail; toggled from the button next to its heading. */
  readonly sidebarCollapsed = model(false);
  /** Below this breakpoint the menubar and the sidebar are replaced by the menu drawer. */
  readonly collapseBelow = input<MmscanNavbarBreakpoint>('lg');
  /** Progress bar under the header while `MmscanLoadingService` reports work (HTTP requests). */
  readonly showLoading = input(true, { transform: booleanAttribute });
  /** Colour navigation icons with the primary colour. */
  readonly accentIcons = input(false, { transform: booleanAttribute });
  /**
   * The header is the title bar of a frameless desktop window (Electron `frame: false`): dragging
   * its background moves the window, while its links, buttons and fields stay clickable.
   */
  readonly titleBar = input(false, { transform: booleanAttribute });

  protected readonly asideClasses = computed(() =>
    cn(
      'hidden shrink-0 overflow-y-auto border-r border-border p-2',
      ASIDE_VISIBLE[this.collapseBelow()],
      !this.sidebarCollapsed() && 'w-64'
    )
  );

  protected skipToContent(event: Event): void {
    event.preventDefault();
    this.main().nativeElement.focus();
  }
}

/** App shell and its slot directives. */
export const MMSCAN_APP_SHELL = [MmscanAppShell, MmscanShellActions, MmscanShellFooter] as const;
